import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import type { DisplayModeConfig } from './data-layers-config';

/**
 * Sensor Fusion specific display modes configuration
 *
 * SENSOR-FUSION MODE:
 *   - Uses activeDataLayers from metadata (AI Trend, Sensors, Oil, Coolant, Alarms, Maintenance)
 *   - Hides: SPECTRUM layer (but keeps it mounted for instant switching)
 *   - Shows: All graphs in SENSORS layer
 *   - UI: Manage Graphs dropdown visible with all options
 *
 * SPECTRUM MODE:
 *   - Override layers: Only SENSORS + SPECTRUM visible
 *   - Hides: All other layers (AI Trend, Oil, Coolant, Alarms, Maintenance)
 *   - In SENSORS layer:
 *     - Manage Graphs dropdown shows ONLY vibration trends option (pre-selected)
 *     - All non-vibration graphs hidden from dropdown
 */
export const SENSOR_FUSION_DISPLAY_MODES: Record<string, DisplayModeConfig> = {
  'sensor-fusion': {
    visibleLayers: [], // Empty = use activeDataLayers from metadata
    hiddenLayers: [SENSOR_FUSION_DATA_LAYERS.SPECTRUM], // Hide SPECTRUM but keep mounted
  },
  spectrum: {
    visibleLayers: [SENSOR_FUSION_DATA_LAYERS.SENSORS, SENSOR_FUSION_DATA_LAYERS.SPECTRUM],
    // Use whitelist approach: SELECT only vibration graphs (doesn't filter the graphs array)
    // This controls which sections are visible without changing the sensor map
    visibleGraphsInLayers: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: (graph: any) => {
        // Check graph title for "vibration"
        const titleHasVibration = (graph.title || '').toLowerCase().includes('vibration');

        // Check legend items for vibration sensor type
        const hasVibrationData = graph.legend?.some((item: any) => {
          const sensorType = item.sensor_type || item.type || '';
          return sensorType.toLowerCase().includes('vibration');
        });

        // Return true to SELECT if either title or legend has vibration
        return titleHasVibration || hasVibrationData;
      },
    },
    autoSelectFilteredGraphs: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: true, // Auto-select all vibration graphs
    },
    hiddenUIElements: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: ['managedGraphsDropdown'], // Hide dropdown in spectrum mode
    },
  },
  waveform: {
    visibleLayers: [SENSOR_FUSION_DATA_LAYERS.SENSORS, SENSOR_FUSION_DATA_LAYERS.WAVEFORM],
    // Same as spectrum: only show vibration graphs
    visibleGraphsInLayers: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: (graph: any) => {
        // Check graph title for "vibration"
        const titleHasVibration = (graph.title || '').toLowerCase().includes('vibration');

        // Check legend items for vibration sensor type
        const hasVibrationData = graph.legend?.some((item: any) => {
          const sensorType = item.sensor_type || item.type || '';
          return sensorType.toLowerCase().includes('vibration');
        });

        // Return true to SELECT if either title or legend has vibration
        return titleHasVibration || hasVibrationData;
      },
    },
    autoSelectFilteredGraphs: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: true, // Auto-select all vibration graphs
    },
    hiddenUIElements: {
      [SENSOR_FUSION_DATA_LAYERS.SENSORS]: ['managedGraphsDropdown'], // Hide dropdown in waveform mode
    },
  },
};
